<template>
    <v-menu v-model="resourcePoolMenu" offset-x nudge-top="8" :close-on-content-click="false">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingAvailableResourcePools"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeResourcePoolId)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-chart-box-outline' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingAvailableResourcePools">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ resourcePoolName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="resourcePoolActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToPoolSettings">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="goToPoolUsers">
                                        <v-list-item-icon>
                                            <v-icon>mdi-account-group-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Users</v-list-item-title>
                                    </v-list-item>
                                    <ResourcePoolInviteDialog ref="resourcePoolInviteDialog" v-if="activeResourcePoolId">
                                        <template v-slot:activator>
                                            <v-list-item @click="openPoolInviteFromActionsMenu">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </ResourcePoolInviteDialog>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ resourcePoolName }}</span>
            </v-tooltip>
        </template>

        <ResourcePoolOptionList
            ref="optionList"
            :items="availableResourcePools"
            :selected-id="activeResourcePoolId"
            :loading="fetchingAvailableResourcePools"
            @select="openResourcePool($event.rpid)" />
    </v-menu>
</template>

<script>
import { mapState } from 'vuex'

const ResourcePoolInviteDialog = () => import('@/modules/user/components/ResourcePoolInviteDialog.vue')
const ResourcePoolOptionList = () => import('@/modules/user/components/ResourcePoolOptionList.vue')

export default {
    name: 'ResourcePoolMenu',
    components: {
        ResourcePoolInviteDialog,
        ResourcePoolOptionList
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            resourcePoolMenu: false,
            resourcePoolActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapState('userStore', ['fetchingAvailableResourcePools', 'availableResourcePools', 'selectedResourcePool']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        activeResourcePoolId() {
            return Number(this.$route.params.rpid || this.selectedResourcePool || this.availableResourcePools[0]?.rpid)
        },
        activeResourcePool() {
            return this.availableResourcePools.find(item => Number(item.rpid) === this.activeResourcePoolId)
        },
        resourcePoolName() {
            return this.activeResourcePool?.long_id || 'Select resource pool'
        }
    },
    methods: {
        goToPoolSettings() {
            this.resourcePoolActionsMenu = false
            if (!this.currentOrgOid || !this.activeResourcePoolId) {
                return
            }

            this.$router.push({
                name: 'config-pool-general',
                params: {
                    oid: this.currentOrgOid,
                    rpid: this.activeResourcePoolId
                }
            })
        },
        goToPoolUsers() {
            this.resourcePoolActionsMenu = false
            if (!this.currentOrgOid || !this.activeResourcePoolId) {
                return
            }

            this.$router.push({
                name: 'config-pool-users',
                params: {
                    oid: this.currentOrgOid,
                    rpid: this.activeResourcePoolId
                }
            })
        },
        openPoolInviteFromActionsMenu() {
            this.resourcePoolActionsMenu = false

            this.$nextTick(() => {
                const inviteDialog = this.$refs.resourcePoolInviteDialog
                if (inviteDialog && inviteDialog.openDialog) {
                    inviteDialog.openDialog()
                }
            })
        },
        openResourcePool(rpid) {
            this.resourcePoolMenu = false

            const routeName = this.$route.name || ''
            if ((routeName === 'config-root' || routeName.startsWith('config-')) && this.currentOrgOid) {
                this.$router.push({
                    name: 'config-pool-general',
                    params: {
                        oid: this.currentOrgOid,
                        rpid
                    }
                })
                return
            }

            this.$router.push({
                name: 'reporting-rp-overview',
                params: {
                    rpid,
                    startDate: this.$route.params.startDate,
                    endDate: this.$route.params.endDate
                }
            })
        },
        syncResourcePoolState() {
            const managedResourcePools = this.availableResourcePools.filter(item => item.is_rp_manager)
            this.$store.dispatch('userStore/setManagedResourcePools', managedResourcePools)

            const selectedResourcePoolId = Number(this.$route.params.rpid || this.selectedResourcePool)
            if (Number.isFinite(selectedResourcePoolId) && selectedResourcePoolId > 0) {
                this.$store.dispatch('userStore/setSelectedResourcePool', selectedResourcePoolId)
            }

            const selectedResourcePool = this.availableResourcePools.find(item => Number(item.rpid) === selectedResourcePoolId) || null
            this.$store.dispatch('userStore/setSelectedRP', selectedResourcePool)
            this.$store.dispatch('userStore/setRpManager', Boolean(selectedResourcePool?.is_rp_manager))
        }
    },
    watch: {
        '$route.params.rpid': {
            handler() {
                this.syncResourcePoolState()
            },
            immediate: true
        },
        availableResourcePools: {
            handler() {
                this.syncResourcePoolState()
            },
            immediate: true
        },
        resourcePoolMenu(val) {
            if (val && this.$refs.optionList) this.$refs.optionList.reset()
        }
    },
    created() {
        if (!this.availableResourcePools.length && !this.fetchingAvailableResourcePools) {
            this.$store.dispatch('userStore/fetchAvailableResourcePoolData')
        }
    }
}
</script>
